import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addMany, addOne, removeOne } from 'redux/actions/cartActions'

const MAX_QUANTITY = 999999

export const useProductQuantity = (product, autoAdd = true) => {
  const dispatch = useDispatch()
  const pendingAddProductId = useSelector(
    state => state.cart.pendingAddProductId
  )

  const [quantity, setQuantity] = useState(product.Quantity)

  const allowFractionalQty = product.AllowFractionalQty
  const inputMask = allowFractionalQty ? /^\d{1,6}(\.\d{1,4})?$/ : /^\d{1,6}$/

  const hasWarranty = useSelector(
    state =>
      product.IsExtendedWarranty ||
      state.cart.cart.some(
        item =>
          item.IsExtendedWarranty &&
          item.WarrantyProductId === product.ProductId
      )
  )

  const handleProdQuantity = e => {
    const value = e.target.value
    const regex = /^\d*\.?\d{0,4}$/
    if (!regex.test(value)) return
    if (!allowFractionalQty && value.includes('.')) return

    // Evitar múltiples ceros al inicio (ej: "0000", "00000")
    if (value.length > 1 && value[0] === '0' && value[1] === '0') return

    // Validación de valor máximo
    if (value !== '') {
      const numericValue = parseFloat(value)
      if (!isNaN(numericValue) && numericValue > MAX_QUANTITY) return
    }

    setQuantity(value === '' ? '' : value)

    if (value === '') {
      return
    }

    // Funciones auxiliares para mayor claridad
    const endsWithDot = () => value.endsWith('.')
    const endsWithDotZero = () => value.endsWith('.0')
    const isIncompleteDecimal = () => allowFractionalQty && endsWithDot()

    // Si el usuario está escribiendo un decimal (termina en punto), no procesar aún
    if (isIncompleteDecimal()) return

    // Determinar el valor a procesar
    // Si termina en ".0", mantener como string, sino convertir a número
    const processedValue = endsWithDotZero() ? value : parseFloat(value)
    const cleanValue = isNaN(processedValue) ? '' : processedValue

    // Solo procesar si es válido y no termina en punto
    if (autoAdd && cleanValue > 0 && !endsWithDot()) {
      const payload = {
        product,
        numberOfProd: parseFloat(value),
      }
      dispatch(addMany(payload))
    }
  }

  // Nueva función para manejar cuando el input pierde el foco
  const handleBlur = () => {
    if (pendingAddProductId === product.ProductId) {
      return
    }

    if (quantity === '' || quantity === null || quantity === undefined) {
      setQuantity('1')

      if (autoAdd && !hasWarranty) {
        const payload = {
          product,
          numberOfProd: 1,
        }
        dispatch(addMany(payload))
      }
    }
  }

  const handleRemoveProduct = () => {
    let newQuantity

    if (quantity === '') {
      newQuantity = 0
    } else {
      const [integerPart, decimalPart] = quantity.toString().split('.')
      const decrementedInteger = parseInt(integerPart) - 1
      newQuantity = decimalPart
        ? `${decrementedInteger}.${decimalPart}`
        : `${decrementedInteger}`
    }

    setQuantity(newQuantity)

    if (autoAdd) {
      dispatch(removeOne({ product, newQuantity: parseFloat(newQuantity) }))
    }
  }

  const handleAddProduct = () => {
    if (quantity >= MAX_QUANTITY) return

    let newQuantity

    if (quantity === '') {
      newQuantity = 1
    } else {
      const [integerPart, decimalPart] = quantity.toString().split('.')
      const incrementedInteger = parseInt(integerPart) + 1
      newQuantity = decimalPart
        ? `${incrementedInteger}.${decimalPart}`
        : `${incrementedInteger}`
    }

    setQuantity(newQuantity)

    if (autoAdd) {
      dispatch(addOne({ product, newQuantity: parseFloat(newQuantity) }))
    }
  }

  useEffect(() => {
    if (hasWarranty && quantity === '') return
    setQuantity(product.Quantity)
  }, [product.Quantity, hasWarranty])

  return {
    handleProdQuantity,
    handleBlur,
    allowFractionalQty,
    quantity,
    handleAddProduct,
    handleRemoveProduct,
    inputMask,
  }
}
